import { Component, inject, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { EToastItemType } from 'projects/shared/lib/enum/toast/toast-entry-type.enum';
import { ToastItem } from 'projects/shared/lib/components/shared/toast/item/toast-item.model';
import { CountryDto, ContactRequestSendCommand } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { StandaloneBaseComponent } from 'projects/shared/lib/components/standalone/base/standalone-base.component';
import { IAddressService, IAddressServiceToken } from 'projects/shared/lib/services/data/address/address-service.interface';
import { IContactRequestService, IContactRequestServiceToken } from 'projects/shared/lib/services/data/contact-request/contact-request-service.interface';
import { BasicError } from 'projects/shared/lib/error/basic-error';
import { ELoadingIndicatorStyle } from 'projects/shared/lib/enum/loading/loading-indicator-style.enum';
import { CountryComboBoxComponent } from 'shared/components/shared/country-combo-box/country-combo-box.component';
import { DirectivesModule } from 'projects/shared/lib/directives/directives.module';
import { LoadingIndicatorComponent } from '../../shared/base/loading-indicator/loading-indicator.component';
import { CheckPrivacyComponent } from '../../shared/check/privacy/check-privacy.component';
import { INavigationEnvironment } from 'projects/shared/lib/interfaces/environment/navigation/navigation-environment.interface';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';

@Component({
  selector: 'app-contact-request',
  imports: [
    FormsModule,
    CountryComboBoxComponent,
    DirectivesModule,
    LoadingIndicatorComponent,
    CheckPrivacyComponent
  ],
  templateUrl: './contact-request.component.html',
  styleUrls: ['./contact-request.component.scss']
})
export class ContactRequestComponent extends StandaloneBaseComponent implements OnInit {
  public company: string | undefined;
  public customerNumber: string | undefined;
  public customerName: string | undefined;
  public telephone: string | undefined;
  public street: string | undefined;
  public zipCode: string | undefined;
  public city: string | undefined;
  public eMail: string | undefined;
  public subject: string | undefined;
  public message: string | undefined;
  public selectedCountry: CountryDto | undefined;

  public shouldSendCopyToContactPerson: boolean;
  public acceptPrivacy: boolean;
  public isTemplateSelected: boolean;
  public countryList: CountryDto[];

  public submitIsDisabled: boolean;

  public inputValidationEmailRegexPattern: string;

  public isPending: boolean;
  public eLoadingIndicatorStyle: typeof ELoadingIndicatorStyle;

  public privacyCheckTarget: INavigationEnvironment;

  private readonly toastService: IToastService = inject(IToastServiceToken);
  private readonly contactRequestService: IContactRequestService = inject(IContactRequestServiceToken);
  private readonly addressService: IAddressService = inject(IAddressServiceToken);

  public constructor() {
    super();

    let title = $localize`:@@ContactFormTitle:Contact request`;
    this.titleSet(title);
    this.metaDescriptionSet([{ key: "page", value: title }]);

    this.shouldSendCopyToContactPerson = true;
    this.acceptPrivacy = false;
    this.submitIsDisabled = false;
    this.countryList = [];
    this.isTemplateSelected = false;

    this.inputValidationEmailRegexPattern = ENVIRONMENT.validation.inputValidationEmailRegexPattern;
    this.eLoadingIndicatorStyle = ELoadingIndicatorStyle;
    this.isPending = false;

    this.privacyCheckTarget = ENVIRONMENT.privacyCheckTarget != undefined ? ENVIRONMENT.privacyCheckTarget : { routerlink: '/content', queryParams: { parent: 'DataProtection' } };
  }

  public async ngOnInit(): Promise<void> {
    await this.addressCountryListLoad();
  }

  public mailSendClick() {
    // validation
    let form = document.getElementById('contactForm') as HTMLFormElement;
    if (form.checkValidity() === false) {
      form.classList.add('was-validated');
      return;
    }

    this.isPending = true;
    // valid
    let command: ContactRequestSendCommand = new ContactRequestSendCommand();
    command.company = this.company;
    command.customerNumber = this.customerNumber;
    command.customerName = this.customerName;
    command.telephone = this.telephone;
    command.street = this.street;
    command.zipCode = this.zipCode;
    command.city = this.city;
    command.eMail = this.eMail;
    command.subject = this.subject;
    command.message = this.message;
    command.eMailCopyToContactPartner = this.shouldSendCopyToContactPerson;
    command.countryCode = this.selectedCountry?.countryCode;

    firstValueFrom(this.contactRequestService.contactRequestSend(command)).then(
      () => {
        this.toastService.toastItemAdd(new ToastItem({
          type: EToastItemType.Success,
          title: $localize`:@@ContactFormSubmitSuccessTitle:Success`,
          message: $localize`:@@ContactFormSubmitSuccessMessage:Your contact message has been received.`,
          autohide: false
        }));
        this.router.navigate(['']);
      }
    ).catch(
      (ex) => {
        this.toastService.toastItemAdd(new ToastItem({
          type: EToastItemType.Warning,
          title: $localize`:@@ContactFormSubmitErrorTitle:Failure`,
          message: $localize`:@@ContactFormSubmitErrorMessage:Your contact message could not be received. Please try again or reach out to us.`,
          code: JSON.parse(ex.response).errorCode,
          autohide: false
        }));
      }
    ).finally(() => {
      this.isPending = false;
    });
  }

  private async addressCountryListLoad() {
    await firstValueFrom(this.addressService.countryListGet(true)).then((data) => {
      if (!data.countryList || data.countryList.length < 1) {
        return;
      }

      this.countryList = data.countryList;
    }).catch((error) => {
      this.logger.warn(error, this.constructor.name);
      let errorMessage = $localize`:@@ContactRequestBaseCountryLoadFailure:Could not load countrys.`;
      throw new BasicError(errorMessage, JSON.parse(error.response).errorCode);
    });
  }

  public acceptPrivacyOnChecked(isChecked: boolean) {
    this.acceptPrivacy = isChecked;
  }

  onCountryCodeChange(value: CountryDto) {
    this.selectedCountry = value;
  }
}
